import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy, inject } from '@angular/core';
import { Router } from '@angular/router';
import { ID, transaction } from '@datorama/akita';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, retry, take } from 'rxjs/operators';
import { FilterParams } from 'src/app/models';
import { environment } from '../../../environments/environment';
import { CATEGORY_ID, Product, ProductFilter, ProductPageRequest, ProductPageResponse } from '../../models/product';
import { AuthService } from '../auth';
import { CategoryService } from '../category/category.service';
import { ContentService } from '../content/content.service';
import { ProductQuery } from './product.query';
import { ProductStore, initialProductState } from './product.store';

@Injectable({ providedIn: 'root' })
export class ProductService implements OnDestroy {
  private router = inject(Router);
  private productStore = inject(ProductStore);
  private productQuery = inject(ProductQuery);
  private http = inject(HttpClient);
  private contentService = inject(ContentService);
  private categoryService = inject(CategoryService);
  private authService = inject(AuthService);

  private readonly productEndpoint = `${environment.apiOrigin}/Product/query`;
  private readonly productMemberEndpoint = `${environment.apiOrigin}/Product/memberquery`;
  private readonly productSearchEndpoint = `${environment.apiOrigin}/Product/AzureSearch`;
  private readonly productsByIdEndpoint = `${environment.apiOrigin}/Product/ids`;

  private subscription: Subscription;

  private requestEndpoint: string;
  private currentSearchId: number;
  public emptyResponse: ProductPageResponse = { total: 0, products: [] };

  constructor() {
    this.subscription = new Subscription();
    this.currentSearchId = 0;

    this.subscription.add(
      this.authService.member
        .pipe(
          distinctUntilChanged((prev, curr) => {
            const customer = this.authService.getCustomerDetails(curr);
            if (customer) {
              return false;
            }
            return prev?.id === curr?.id;
          }),
        )
        .subscribe((member) => {
          this.requestEndpoint = member ? this.productMemberEndpoint : this.productEndpoint;
          this.productStore.remove();
        }),
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  async searchProducts(reset: boolean): Promise<void> {
    const isOverview = this.productQuery.getSearchOverview();
    const searchTerm = this.productQuery.getSearchTerm();
    const filters = this.productQuery.getSearchFilters();

    if (isOverview && searchTerm && filters?.categoryId) {
      this.router.navigate([`/${environment.urls.products}`]);
      return new Promise<void>((resolve) => resolve());
    }

    if (!isOverview && searchTerm && filters) {
      this.updateSearchFilters(undefined);
    }

    if (reset) {
      this.productStore.resetSearch();
    }

    const searchId = ++this.currentSearchId;
    const pageSize = isOverview ? this.productQuery.getPageSize() : this.productQuery.getSearchPageSize();
    const pageNumber = this.productQuery.getPage();
    const categories = this.categoryService.getCurrentCategoryIds();

    return this.get(searchId, pageNumber, pageSize, isOverview, searchTerm, categories, filters) as Promise<void>;
  }

  get(
    searchId: number,
    PageNumber: number,
    PageSize: number,
    isOverview: boolean,
    SearchTerm: string,
    Categories?: number[],
    filters?: FilterParams<ProductFilter>,
    updateStore = true,
  ): Promise<void> | Observable<ProductPageResponse> {
    this.productStore.setLoading(true);

    const MerchantId = this.contentService.getMerchantId();

    const params: ProductPageRequest = {
      MerchantId,
      PageSize,
      PageNumber,
      SearchTerm: SearchTerm ? SearchTerm : '',
      DisplayInStore: true,
    };

    if (Categories && Categories.length) {
      params.CategoryIds = Categories;
    }

    if (this.authService.isLoggedIn()) {
      const id = this.authService.getCustomerDetails(this.authService.member.value)?.id;
      if (id) {
        params.CustomerId = id;
      }
    }

    let endpoint = this.requestEndpoint;
    if (SearchTerm) {
      endpoint = this.productSearchEndpoint;
    }

    const request = this.http
      .get<ProductPageResponse>(endpoint, {
        params,
      })
      .pipe(retry({ count: 2, delay: 1000 }), take(1));

    if (!updateStore) {
      return request;
    }

    return new Promise<void>((resolve) => {
      request.subscribe({
        next: (res) => {
          this.updateProducts(
            res,
            () => {
              const currentResults = this.productQuery.getSearchResults();
              const ids = res.products.map(({ id }) => id);
              this.productStore.update({ searchResults: currentResults.concat(ids) });
              this.productStore.update({ searchOpen: !isOverview });
            },
            searchId,
          );

          this.productStore.setLoading(false);
          resolve();
        },
        error: (error) => {
          this.productStore.setLoading(false);
          resolve();
        },
      });
    });
  }

  updateSearchTerm(searchTerm: string) {
    if (this.productQuery.getSearchTerm() === searchTerm) {
      return false;
    }

    this.productStore.update({ searchTerm });
    return true;
  }

  updateSearchFilters(searchFilters: FilterParams<ProductFilter>) {
    this.productStore.update({ searchFilters });

    let activeFilter = false;
    if (searchFilters) {
      for (const [key, value] of Object.entries(searchFilters)) {
        if (value === true.toString()) {
          activeFilter = true;
        }

        if (key === CATEGORY_ID) {
          activeFilter = true;
        }
      }
    }

    if (activeFilter) {
      this.updateSearchTerm('');
    }
  }

  updateSearchOverview(searchOverview: boolean) {
    this.productStore.update({ searchOverview });
  }

  closeSearch() {
    this.productStore.update({ searchOpen: false });
  }

  async getFrontpageProducts(pageSize: number, categoryId: number) {
    return new Promise<void>((resolve) => {
      (this.get(null, 0, pageSize, false, '', [categoryId], null, false) as Observable<ProductPageResponse>).subscribe((res) =>
        this.updateProducts(res, () => {
          const frontpageProducts = [...this.productStore.getValue().frontpageProducts];
          frontpageProducts.push(res.products.map(({ id }) => id));
          this.productStore.update({ frontpageProducts });
          resolve();
        }),
      );
    });
  }

  getSuggestedProducts(pageSize: number) {
    // TODO: random products, new endpoint?
    (this.get(null, 0, pageSize, false, '', null, null, false) as Observable<ProductPageResponse>).subscribe((res) =>
      this.updateProducts(res, () => {
        const ids = res.products.map(({ id }) => id);
        this.productStore.update({ suggestedProducts: ids });
      }),
    );
  }

  createProductUrl(product: Product): string {
    const namePart = product.productUrl || product.productDisplayName.toLowerCase().replace(/\s/g, '-');
    const id = product.id;

    return `/${environment.urls.products}/${namePart}-${id}`;
  }

  @transaction()
  private updateProducts(pagedResponse: ProductPageResponse, updates?: () => void, id?: number) {
    if (id && id < this.currentSearchId) {
      return;
    }

    this.productStore.update({ pagedResponse });
    this.productStore.add(pagedResponse.products);

    const { page, pageSize } = this.productQuery.getValue();
    const nextPage = page + 1;
    const hasMore = pagedResponse.total > nextPage * pageSize;
    this.productStore.updatePage({ hasMore, page: nextPage });

    if (updates) {
      updates();
    }
  }

  resetProducts() {
    let frontpageProducts = this.productQuery.getFrontpageProducts();
    const frontpageIds = frontpageProducts.flat();

    this.productStore.update({
      ...initialProductState,
    });
    if (!frontpageIds?.length) return;

    const merchantId = this.contentService.getMerchantId();
    const request = this.http
      .post<ProductPageResponse>(this.productsByIdEndpoint, {
        ids: frontpageIds,
        merchantId,
      })
      .pipe(retry({ count: 2, delay: 1000 }), take(1));

    this.currentSearchId = 1;
    request.subscribe({
      next: (res) => {
        this.updateProducts(
          res,
          () => {
            const resultIds = res.products.map(({ id }) => id as ID);
            frontpageProducts = frontpageProducts.map((category) => {
              category.filter((productId) => resultIds.includes(productId));
              return category;
            });
            this.productStore.update({ frontpageProducts });
          },
          this.currentSearchId,
        );

        this.productStore.setLoading(false);
      },
      error: (error) => {
        this.productStore.setLoading(false);
      },
    });
  }
}
